import { useState } from 'react';
import { toast } from 'sonner';
import { simulate } from '../lib/aquaNet';
import { tr } from '../lib/tankRoleText';

const nf = (v, d = 2) => (Number.isFinite(+v) ? (+v).toFixed(d).replace('.', ',') : '–');

// every pipe connected to a tank / reservoir: fill pipe (top or bottom inlet), supply pipe or both directions
export function TankPipes({ obj, h, set, project }) {
  const x = tr(), list = (project?.pipes || []).filter((p) => (p.points || []).some((pt) => pt.fid === obj.id));
  const roles = h.roles || {};
  return (
    <div className="aq-tank-pipes" data-testid="aq-tank-pipes">
      <b>{x.pipes} ({list.length})</b>
      {!list.length && <small className="muted">{x.none}</small>}
      {list.map((p) => (
        <label key={p.id} className="aq-row">
          <span>{p.name || p.id}</span>
          <select data-testid={`aq-tank-role-${p.id}`} value={roles[p.id] || 'both'} onChange={(e) => set({ roles: { ...roles, [p.id]: e.target.value } })}>
            {Object.entries(x.role).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
      ))}
      {list.length > 0 && <small className="muted">{x.hint}</small>}
    </div>
  );
}

const leaksOff = (p) => ({ ...p, fittings: (p.fittings || []).map((f) => (f.type === 'leak' ? { ...f, hyd: { ...(f.hyd || {}), leakOn: false } } : f)) });
// fill pipes of all tanks: inflow in l/s (positive into the tank)
const inflows = (project, res) => (project.fittings || []).filter((f) => f.hyd?.roles).flatMap((f) => Object.entries(f.hyd.roles).filter(([, r]) => r.startsWith('fill')).map(([pid]) => {
  const ls = res.links.filter((l) => l.pipeId === pid && [l.a, l.b].some((n) => res.nodes?.[n]?.fid === f.id));
  return { key: `${f.id}${pid}`, name: f.name, q: ls.length ? Math.max(...ls.map((l) => Math.abs(l.q))) : 0 };
}));

// hydrant: same network with the active leaks and without -> static / flow pressure and tank inflow side by side
export function LeakCompare({ obj, project }) {
  const x = tr(), [busy, setBusy] = useState(false), [res, setRes] = useState(null);
  const on = (project.fittings || []).some((f) => f.type === 'leak' && f.hyd?.leakOn);
  const run = async () => {
    setBusy(true);
    try {
      const cfg = project.aqua || {}, [a, b] = [await simulate(project, cfg), await simulate(leaksOff(project), cfg)];
      if (a.error || b.error) { toast.error(String(a.error || b.error)); setBusy(false); return; }
      const hy = (r) => r.hyd?.find((z) => z.fid === obj.id) || {}, lq = (r) => (project.fittings || []).filter((f) => f.type === 'leak' && f.hyd?.leakOn).reduce((s, f) => s + (Math.abs(r.nodes?.[Object.keys(r.nodes).find((k) => r.nodes[k].fid === f.id)]?.demand) || 0), 0);
      setRes({ a: { ...hy(a), inf: inflows(project, a), lq: lq(a) }, b: { ...hy(b), inf: inflows(project, b) } });
    } catch (e) { toast.error(String(e?.message || e)); }
    setBusy(false);
  };
  if (!on) return <small className="muted" data-testid="aq-cmp-noleak">{x.noLeak}</small>;
  const row = (k, l, fa, fb, d) => <tr key={k}><td>{l}</td><td>{fa}</td><td>{fb}</td><td>{d}</td></tr>;
  return (
    <div className="aq-cmp" data-testid="aq-leak-compare">
      <button type="button" className="btn btn-accent btn-sm" data-testid="aq-cmp-btn" disabled={busy} onClick={run}>⚖️ {busy ? x.cmpBusy : x.cmp}</button>
      {res && <table className="aq-cmp-t" data-testid="aq-cmp-table"><thead><tr><th /><th>{x.withL}</th><th>{x.noL}</th><th>{x.diff}</th></tr></thead><tbody>
        {row('s', x.stat, `${nf(res.a.stat)} bar`, `${nf(res.b.stat)} bar`, `${nf((res.a.stat ?? 0) - (res.b.stat ?? 0))} bar`)}
        {res.a.q > 0 && row('f', `${x.flow} (${nf(res.a.q, 1)} l/s)`, `${nf(res.a.flow)} bar`, `${nf(res.b.flow)} bar`, `${nf((res.a.flow ?? 0) - (res.b.flow ?? 0))} bar`)}
        {res.a.inf.map((z, i) => row(z.key, x.inflow.replace('{n}', z.name || ''), `${nf(z.q)} l/s`, `${nf(res.b.inf[i]?.q)} l/s`, `${nf(z.q - (res.b.inf[i]?.q || 0))} l/s`))}
        {row('l', x.leakQ, `${nf(res.a.lq)} l/s`, '0 l/s', '')}
      </tbody></table>}
    </div>
  );
}
